import type { ActivityDataPoint } from "../../../../ActivityChart/types";
import type { GraphData, DetailedQuestionDataPoint, QuizComponents } from "../types";

/**
 * Функция для построения серии данных для режима "по минутам"
 * @param data - массив количества ответов по минутам
 * @param normalThreshold - пороговое значение для определения нормы
 */
export function buildMinutesSeries(
  data: number[],
  normalThreshold: number
): ActivityDataPoint[] {

  if (!data || !Array.isArray(data)) {
    return [];
  }

  const result = data.map((value, index) => ({
    minute: index + 1,  // Индекс массива становится номером минуты (1-based)
    value,              // Плотность вопросов в эту минуту
    isNormal: value <= normalThreshold,
    isBurst: false,
  }));

  return result;
}

/**
 * Функция для построения серии данных для режима "по вопросам"
 * @param data - массив количества ответов по минутам
 * @param normalThreshold - пороговое значение для времени на вопрос (в секундах)
 */
export function buildQuestionsSeries(
  data: number[],
  normalThreshold: number
): ActivityDataPoint[] {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  // Формула из задачи:
  // 1. total_questions = сумма всех элементов results_chart.data
  const total_questions = data.reduce((sum, value) => sum + value, 0);

  // 2. active_minutes = количество минут, где data[i] > 0
  const active_minutes = data.filter(value => value > 0).length;

  // 3. active_time_sec = active_minutes * 60
  const active_time_sec = active_minutes * 60;

  // 4. time_per_question_sec = active_time_sec / total_questions
  const time_per_question_sec = total_questions > 0 ? active_time_sec / total_questions : 0;

  if (total_questions === 0) {
    return [];
  }

  // Генерируем массив длины total_questions, где каждый элемент = time_per_question_sec
  const result: ActivityDataPoint[] = [];
  for (let i = 0; i < total_questions; i++) {
    result.push({
      minute: i + 1,
      value: time_per_question_sec,  // Время на вопрос в секундах
      isNormal: time_per_question_sec <= normalThreshold,
      isBurst: false,
    });
  }

  return result;
}

/**
 * Функция для построения детальных данных "по вопросам"
 * Использует quiz_components с attempts для построения реального timeline ответов
 * @param quizComponents - компоненты квиза с попытками ответов
 */
export function buildDetailedQuestionsSeries(
  quizComponents: QuizComponents
): DetailedQuestionDataPoint[] {
  if (!quizComponents || !Array.isArray(quizComponents) || quizComponents.length === 0) {
    return [];
  }

  // Собираем все attempts из всех компонентов с индексом в исходном массиве
  const allAttempts: Array<{
    created_at: string;
    result: number;
    componentIndex: number; // индекс в массиве quizComponents (для правильного отображения)
    originalPosition: number; // оригинальная позиция из БД
  }> = [];

  quizComponents.forEach((component, index) => {
    const position = component.position;

    if (component.component && Array.isArray(component.component.attempts)) {
      component.component.attempts.forEach((attempt) => {
        if (attempt.created_at && typeof attempt.result === 'number') {
          allAttempts.push({
            created_at: attempt.created_at,
            result: attempt.result,
            componentIndex: index,        // индекс для отображения номера как в интерфейсе
            originalPosition: position    // оригинальная позиция из БД
          });
        }
      });
    }
  });

  if (allAttempts.length === 0) {
    return [];
  }

  // Сортируем по времени создания (created_at ASC)
  allAttempts.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  // Создаем данные для графика
  const result: DetailedQuestionDataPoint[] = [];

  for (let i = 0; i < allAttempts.length; i++) {
    const current = allAttempts[i];
    let timeInSeconds = 0;

    if (i === 0) {
      // Для первого ответа: устанавливаем базовое время
      timeInSeconds = 30; // 30 секунд для первого ответа
    } else {
      // time[i] = created_at[i] - created_at[i - 1] (в секундах)
      const previous = allAttempts[i - 1];
      const currentTime = new Date(current.created_at).getTime();
      const previousTime = new Date(previous.created_at).getTime();
      timeInSeconds = Math.round((currentTime - previousTime) / 1000); // миллисекунды -> секунды
    }

    result.push({
      pos: i + 1,                              // хронологический порядок ответа (1-based)
      questionPos: current.componentIndex + 1, // номер вопроса как в интерфейсе (1-based)
      time: timeInSeconds,                     // время между ответами в секундах
      result: current.result                   // correct / wrong
    });
  }

  return result;
}

/**
 * Функция для построения данных "по вопросам" из тех же источников что и блоки статусов
 * Использует entity.quiz_components и questionResults для синхронизации с ResultsTab
 */
export function buildQuestionSeriesFromResults(
  quizComponents: any[],
  questionResults: any[],
  attemptId?: number | null
): DetailedQuestionDataPoint[] {
  if (!quizComponents || !Array.isArray(quizComponents) || quizComponents.length === 0) {
    return [];
  }

  if (!questionResults || !Array.isArray(questionResults)) {
    return [];
  }

  // Собираем все attempts со связанными компонентами для вычисления времени
  const allAttempts: Array<{
    created_at: string;
    result: number;
    componentIndex: number; // индекс в исходном массиве quizComponents
    component_id: number;
  }> = [];

  quizComponents.forEach((q, index) => {
    const manualResult = questionResults.find(
      (r) =>
        r.component_id === q.id &&
        r.component_type === q.component_type &&
        (attemptId == null || r.assignment_attempt_id === attemptId)
    );

    // Берем время из attempts или из questionResults
    let attemptTime: string | null = null;
    let resultValue = 0;

    if (q.component && Array.isArray(q.component.attempts) && q.component.attempts.length > 0) {
      // Берем последнюю попытку
      const attempt = q.component.attempts[q.component.attempts.length - 1];
      if (attempt.created_at) {
        attemptTime = attempt.created_at;
        resultValue = attempt.result || 0;
      }
    }

    // Перезаписываем результат из questionResults если есть
    if (manualResult) {
      resultValue = manualResult.result || 0;
      // Пытаемся взять время из questionResults если доступно
      if (manualResult.created_at) {
        attemptTime = manualResult.created_at;
      }
    }

    if (attemptTime) {
      allAttempts.push({
        created_at: attemptTime,
        result: resultValue,
        componentIndex: index,
        component_id: q.id
      });
    }

  });

  if (allAttempts.length === 0) {
    return [];
  }

  // Сортируем попытки по времени создания (хронологический порядок ответов)
  allAttempts.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  // Вычисляем время между ответами в хронологическом порядке
  const timeMap = new Map<number, number>(); // componentIndex -> timeInSeconds

  for (let i = 0; i < allAttempts.length; i++) {
    const current = allAttempts[i];
    let timeInSeconds = 0;

    if (i === 0) {
      // Для первого ответа устанавливаем базовое время
      timeInSeconds = 30; // 30 секунд для первого ответа
    } else {
      // time[i] = created_at[i] - created_at[i - 1] (в секундах)
      const previous = allAttempts[i - 1];
      const currentTime = new Date(current.created_at).getTime();
      const previousTime = new Date(previous.created_at).getTime();
      timeInSeconds = Math.round((currentTime - previousTime) / 1000);
    }

    // Сохраняем время для этого компонента
    timeMap.set(current.componentIndex, timeInSeconds);
  }

  // Теперь создаем результат в том же порядке что и в интерфейсе (по индексам)
  const result: DetailedQuestionDataPoint[] = [];

  quizComponents.forEach((q, index) => {
    const manualResult = questionResults.find(
      (r) =>
        r.component_id === q.id &&
        r.component_type === q.component_type &&
        (attemptId == null || r.assignment_attempt_id === attemptId)
    );

    // Берем время из карты времени или устанавливаем дефолтное значение
    const timeInSeconds = timeMap.get(index) || 0;

    let resultValue = 0;
    if (manualResult) {
      resultValue = manualResult.result || 0;
    } else if (q.component && Array.isArray(q.component.attempts) && q.component.attempts.length > 0) {
      const attempt = q.component.attempts[q.component.attempts.length - 1];
      resultValue = attempt.result || 0;
    }

    // Добавляем только если есть ответ
    if (timeInSeconds > 0) {
      result.push({
        pos: index + 1,                    // номер позиции в интерфейсе (как в ResultsTab)
        questionPos: index + 1,            // тот же номер для отображения
        time: timeInSeconds,               // время ответа на этот вопрос
        result: resultValue                // правильность ответа
      });
    }
  });

  // Сортируем по questionPos чтобы график отображался в правильном порядке
  result.sort((a, b) => a.questionPos - b.questionPos);

  return result;
}

/**
 * Парсит данные графика из AI отчета
 * @param graphData - данные графика из AI отчета
 * @param normalThreshold - пороговое значение для нормы
 * @returns массив точек данных для графика активности
 */
export function parseResultsChart(
  graphData: unknown,
  normalThreshold: number
): ActivityDataPoint[] {

  if (!graphData || typeof graphData !== "object") {
    return [];
  }

  const data = graphData as GraphData;
  const resultsChart = data.results_chart;

  if (!resultsChart || !Array.isArray(resultsChart) || resultsChart.length === 0) {
    return [];
  }

  // Ищем элемент с name="results" для QDensity данных
  const qDensityItem = resultsChart.find(item => item.name === "results");
  const chartData = qDensityItem?.data;

  if (!chartData || !Array.isArray(chartData)) {
    return [];
  }

  return buildMinutesSeries(chartData, normalThreshold);
}

/**
 * Парсит данные отчета о назначении
 * @param resultsChart - данные графика из отчета о назначении
 * @param normalThreshold - пороговое значение для нормы
 * @returns массив точек данных для графика активности
 */
export function parseAssignmentReportData(
  resultsChart: Array<{ name: string; data: number[] }> | undefined,
  normalThreshold: number
): ActivityDataPoint[] {
  if (!resultsChart || !Array.isArray(resultsChart) || resultsChart.length === 0) {
    return [];
  }

  // Ищем элемент с name="results" для QDensity данных
  const qDensityItem = resultsChart.find(item => item.name === "results");
  const chartData = qDensityItem?.data;

  if (!chartData || !Array.isArray(chartData)) {
    return [];
  }

  return buildMinutesSeries(chartData, normalThreshold);
}